import React from 'react';

type ResizeObserverFactory = new (callback: () => void) => {
  observe: (target: Element) => void;
  disconnect: () => void;
};

function getResizeObserver(): ResizeObserverFactory | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as Window & { ResizeObserver?: ResizeObserverFactory }).ResizeObserver;
}

function readSize(node: HTMLElement) {
  return {
    height: node.clientHeight,
    width: node.clientWidth,
  };
}

export function useMeasuredHeight() {
  const [size, setSize] = React.useState({ height: 0, width: 0 });
  const [node, setNode] = React.useState<HTMLDivElement | null>(null);

  const ref = React.useCallback((el: HTMLDivElement | null) => {
    setNode(el);
  }, []);

  React.useEffect(() => {
    if (!node) return;

    const updateSize = () => {
      setSize(readSize(node));
    };

    updateSize();

    const ResizeObserverCtor = getResizeObserver();
    if (!ResizeObserverCtor) {
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }

    const observer = new ResizeObserverCtor(updateSize);
    observer.observe(node);

    return () => observer.disconnect();
  }, [node]);

  return { ref, height: size.height, width: size.width };
}
